import * as angular from 'angular';
import { config } from 'angular/utils/config';
import { ShipperResource } from 'app/_resources/shipper.resource';
import { UtilService } from 'app/_services/util.service';
import { Decimal } from 'decimal.js';
import * as moment from 'pkp-moment-timezone-all-years';

export class ProposalResponseCtrl implements ng.IController {
    private INDEX_FIRST_TSO = 0;
    private INDEX_SECOND_TSO = 1;

    public form: any = {};
    public proposalId;
    public formSubmitting = false;
    public minimumLotRuntimeViolation = false;
    /**
     * The proposal details of the proposal to respond... basically ProposalDto.
     *
     * @type {Object}
     */
    public proposal: any = {};
    /**
     * The model-bound values that should then be submitted to the endpoint.
     * Create dummy runtimeObj and price so that watches does not fail
     *
     * @type {Object}
     */
    public response: any = {
        components: [{ underlyingDealId: {} }, { underlyingDealId: {} }]
    };

    /**
     * ProposalDetails for two way binding.
     *
     * @type {Object}
     */
    public proposalDetails: any = {};
    public config: any = {};
    public tsoConfig = [];
    public options = [];
    public underlyingDeals = [];
    public runtimeObj = { start: {}, end: {} };
    public expirationDate: any = {};
    public selectedUnderlyingDeals = [];

    public remitInfoLink: any;
    /**
     * The model-bound value that represents a value of {@link public options[0|1].capacitySources}.
     * Part of the form, but not part of {@link public response} as it should not be sent to the endpoint.
     *
     * @type {String}
     */
    public capacitySource = [];
    public traderLists = [];
    public ui = {
        isRuntimeValid: true,
        isRuntimeStartInPast: false,
        isRuntimeStartGasDayOrAfter: true,
        isRuntimeInProposal: true,
        isExpirationDateSet: true,
        isDeadlineAfterExpiration: false,
        isDeadlineCurrentOrPast: false,
        isTariffUpdated0: false,
        isTariffUpdated1: false,
        isTariffUpdating0: false,
        isTariffUpdating1: false,
        isRegulatedTariffUpdateStateValid: true,
        isMinimumTradeRuntimeValid: true,
        renderUpdateButton0: false,
        renderUpdateButton1: false,
        isMinimumTradeRuntimeValidityMessages: undefined
    };
    public regulatedTariff0: boolean;
    public regulatedTariff1: boolean;
    public isSellIntention: boolean;
    public isTransferOfUse: boolean;

    public conditionsList: any;
    public minimumTradeRuntime: any;

    public balancingCodeInfo: any;
    public channelConfig: any;

    public uiLoadingUnderlyingDeals: boolean;

    constructor(
        public $route: ng.route.IRouteService,
        public $scope: ng.IScope,
        public $filter: ng.IFilterService,
        public $routeParams: ng.route.IRouteParamsService,
        public $q: ng.IQService,
        public utilService: UtilService,
        public $window: ng.IWindowService,
        public TradeProposals,
        public TraderList,
        public TradeCondition,
        public Shipper: ShipperResource,
        public createProposalService,
        public UNDERLYING_DEAL_SOURCE,
        public INCLUDE,
        public $location: ng.ILocationService
    ) {
        this.proposalId = $routeParams.id;

        this.TraderList.specificQuery({}, (res) => {
            this.traderLists = res;
        });
    }

    // If the condition is true, a regulated tariff is required but the UI-Field is disabled
    // so we have to set it to true to be able to continue.
    private getRegulatedTariffDefault(tsoIndex): boolean {
        let regulatedTariffRequired =
            !this.tsoConfig[tsoIndex].prefillPrice &&
            !this.tsoConfig[tsoIndex].capturePriceManually &&
            this.tsoConfig[tsoIndex].applyRegulatedTariff;
        let regulatedTariffFieldDisabled =
            this.tsoConfig[tsoIndex].creditLimitCheckEnabled;
        if (
            this.tsoConfig != null &&
            this.tsoConfig[tsoIndex] != null &&
            regulatedTariffRequired &&
            regulatedTariffFieldDisabled
        ) {
            return true;
        }
        return false;
    }

    public inheritProposalDetails(proposalDetails) {
        this.proposalDetails = proposalDetails;
        this.proposalDetails.ui = {
            updateRegulatedTariffFailed0: false,
            updateRegulatedTariffFailed1: false
        };

        this.proposal = angular.copy(proposalDetails.proposal);

        this.isSellIntention =
            this.proposal.proposalType ===
            this.createProposalService.INTENTION.SELL;
        this.isTransferOfUse =
            this.proposal.transactionType ===
            this.createProposalService.TRANSACTION_TYPE.TRANSFER;

        this.TraderList.specificQuery({}, (res) => {
            this.traderLists = res;
        });

        if (this.proposal.tradingProcedure === 'OTC') {
            this.TradeCondition.query(
                { includePlatformConditions: true },
                (res) => {
                    this.conditionsList = res;
                }
            );
        }

        const promises = [
            this.fetchConfig(),
            this.fetchChannelConfig(),
            this.fetchInfoLink(),
            this.fetchBalancingCodes(
                this.proposal.runtime,
                this.proposal.networkPoint,
                this.proposal.components[0].capacityCategory,
                this.proposal.networkPoint.bundled
                    ? this.proposal.components[1].capacityCategory
                    : undefined
            )
        ];
        this.$q.all(promises).finally(() => {
            this.mapConfigAndOptions();
            this.setModelDefaults();
            this.initWatches();
            this.updateExchangeRates();
            this.minimumTradeRuntime = this.getMinimumRuntime();
            this.regulatedTariff0 = this.getRegulatedTariffDefault(
                this.INDEX_FIRST_TSO
            );
            this.regulatedTariff1 = this.getRegulatedTariffDefault(
                this.INDEX_SECOND_TSO
            );
        });
    }

    public updateExchangeRates() {
        let xchgRate = null;
        if (
            !angular.isUndefined(this.config.tso0Config) &&
            !angular.isUndefined(this.config.tso0Config.exchangeRate)
        ) {
            xchgRate = this.config.tso0Config.exchangeRate;
            this.updateExchangeRate(xchgRate, 'exchangeRate0');
            this.updateExchangeRateFromPrice(xchgRate);
        }
        if (
            !angular.isUndefined(this.config.tso1Config) &&
            !angular.isUndefined(this.config.tso1Config.exchangeRate)
        ) {
            xchgRate = this.config.tso1Config.exchangeRate;
            this.updateExchangeRate(xchgRate, 'exchangeRate1');
            this.updateExchangeRateFromPrice(xchgRate);
        }
    }

    public updateExchangeRateFromPrice(xchgRate) {
        if (
            !angular.isUndefined(this.response.price.originalUnit) &&
            this.response.price.originalUnit.currency === xchgRate.isoCode
        ) {
            this.updateExchangeRate(xchgRate, 'exchangeRate');
        }
    }

    public updateExchangeRate(xchgRate, vmPropertyName) {
        this[vmPropertyName] = {
            value: xchgRate.rate,
            unit: 'EUR/' + xchgRate.isoCode,
            date: moment(xchgRate.validity.start).format('DD.MM.YYYY HH:mm')
        };
    }

    public initWatches() {
        // trigger custom validations every time inputs changes,
        // otherwise errors shows on the corresponding controls stay in UI until next form submit
        this.$scope.$watch(
            () => {
                return this.response.price.originalValue;
            },
            () => {
                this.isPriceInformationValid();
            }
        );

        this.$scope.$watchCollection(
            () => {
                return this.runtimeObj;
            },
            () => {
                if (this.proposal.length > 0) {
                    this.isRuntimeValid();
                }
                this.ui.isTariffUpdated0 = false;
                this.ui.isTariffUpdated1 = false;
                this.proposalDetails.ui.updateRegulatedTariffFailed0 = false;
                this.proposalDetails.ui.updateRegulatedTariffFailed1 = false;
            }
        );
        this.$scope.$watch(
            () => {
                return this.expirationDate;
            },
            () => {
                if (!!this.form) {
                    this.isDeadlineValid();
                }
            }
        );

        if (!this.isSellIntention) {
            this.$scope.$watch(
                () => {
                    if (this.response.components[0].underlyingDealId) {
                        return this.response.components[0].underlyingDealId
                            .source;
                    }
                },
                (newVal) => {
                    if (!newVal) {
                        return;
                    }

                    if (
                        this.response.components[0].underlyingDealId.source !==
                        undefined
                    ) {
                        this.checkRenderUpdateButton('0');
                        this.getUnderlyingDeals(0);

                        if (
                            this.proposal.networkPoint.bundled &&
                            !this.response.combineUnbundledDeals
                        ) {
                            this.response.components[1].underlyingDealId =
                                angular.copy(
                                    this.response.components[0].underlyingDealId
                                );
                            this.checkRenderUpdateButton('1');
                        }
                    }
                }
            );

            if (this.proposal.networkPoint.bundled) {
                this.$scope.$watch(
                    () => {
                        if (this.response.components[1].underlyingDealId) {
                            return this.response.components[1].underlyingDealId
                                .source;
                        }
                    },
                    () => {
                        if (
                            this.response.components[1].underlyingDealId
                                .source !== undefined
                        ) {
                            this.checkRenderUpdateButton('1');
                            this.getUnderlyingDeals(1);
                        }
                    }
                );
            }
        }
    }

    /**
     * Sets default values for the form as well as for the general submission data of {@link submitProposalResponse}.
     * Basically takes given information of the back-end ProposalDto and pastes them into the RespondDto.
     */
    public setModelDefaults() {
        this.runtimeObj = angular.copy(this.proposal.runtime);
        this.response = {
            runtime: angular.copy(this.proposal.runtime),
            amount: angular.copy(this.proposal.amount),
            price: angular.copy(this.proposal.price),
            components: angular.copy(this.proposal.components)
        };

        this.setDefaultsForComponents(0);
        this.setDefaultsForComponents(1);
    }

    /**
     * Set defaults like in {@link setModelDefaults} for the components of the nack-end RespondDto.
     *
     * @param {Number} componentIndex The component to update. Should be either 0 or 1.
     *  Numbers represent network point bundles like with tso0Name and tso1Name.
     */
    public setDefaultsForComponents(componentIndex) {
        var component = this.response.components[componentIndex];
        if (!component) {
            return;
        }

        if (
            this.proposal.proposalType !==
            this.createProposalService.INTENTION.SELL
        ) {
            if (angular.isUndefined(component.underlyingSurcharge)) {
                component.underlyingSurcharge = {
                    originalValue: { value: null, floatingPointNumber: true },
                    originalUnit: this.config.supportedPriceUnits[0]
                };
            }

            if (angular.isUndefined(component.underlyingRegulatedTariff)) {
                component.underlyingRegulatedTariff = {
                    originalValue: { value: null, floatingPointNumber: true },
                    originalUnit: this.config.supportedPriceUnits[0]
                };
            }

            component.underlyingDealId = {};
        } else {
            delete component.underlyingSurcharge;
            delete component.underlyingRegulatedTariff;
            delete component.underlyingDealId;
        }

        delete component.capacityCategory;
        delete component.exchangeRate;
    }

    /**
     * Get underlying deals based on the capacity source {@link this.capacitySource}.
     * Is an action listener of the capacity source select field.
     */
    public getUnderlyingDeals(index) {
        this.uiLoadingUnderlyingDeals = true;

        if (this.isManualCapacitySource(index)) {
            this.uiLoadingUnderlyingDeals = false;
            this.response.components[
                index
            ].underlyingRegulatedTariff.originalUnit =
                this.tsoConfig[index].priceUnit;
            this.response.components[index].underlyingSurcharge.originalUnit =
                this.tsoConfig[index].priceUnit;
        } else {
            delete this.underlyingDeals[index];
            delete this.selectedUnderlyingDeals[index];
            this.TradeProposals.underlyingDeals(
                {
                    networkPointId: this.response.combineUnbundledDeals
                        ? this.proposal.networkPoint['id' + index]
                        : this.proposal.networkPoint.id,
                    underlyingDealSource:
                        this.response.components[index].underlyingDealId.source,
                    runtimeStart: this.proposal.runtime.start,
                    runtimeEnd: this.proposal.runtime.end
                },
                (res) => {
                    if (!!res.length) {
                        this.underlyingDeals[index] = res;
                    }
                },
                () => {
                    this.proposalDetails.uiErrorLoadingUnderlyingDeals = true;
                }
            ).$promise.finally(() => {
                this.uiLoadingUnderlyingDeals = false;
            });
        }
    }

    public selectedUnderlyingDeal(index) {
        if (index === '0') {
            this.updateCharges(0, 0);
            this.prefillCapacityInformation(0, 0);

            if (
                this.proposal.networkPoint.bundled &&
                !this.response.combineUnbundledDeals
            ) {
                this.selectedUnderlyingDeals[1] =
                    this.selectedUnderlyingDeals[0];
                this.updateCharges(1, 1);
                this.prefillCapacityInformation(1, 1);
            }
        } else if (index === '1') {
            this.updateCharges(1, 0);
            this.prefillCapacityInformation(1, 0);
        }
    }

    public updateCharges(tsoIndex, contractIndex) {
        if (
            this.proposal.transactionType === 'ASSIGNMENT' &&
            this.proposal.proposalType === 'PROPOSAL_TO_BUY'
        ) {
            var contract = this.selectedUnderlyingDeals[tsoIndex];
            var resetTo = {
                originalValue: { value: '0', floatingPointNumber: true },
                originalUnit: this.config.supportedPriceUnits[0]
            };

            var auctionSurchargeCopy = this.copyOrReset(
                contract,
                'auctionSurcharge' + contractIndex,
                resetTo
            );
            this.response.components[tsoIndex].underlyingSurcharge =
                auctionSurchargeCopy.obj;

            if (
                this.tsoConfig[tsoIndex].prefillPrice &&
                !this.tsoConfig[tsoIndex].capturePriceManually
            ) {
                var regulatedTariffCopy = this.copyOrReset(
                    contract,
                    'regulatedCapacityTariff' + contractIndex,
                    resetTo
                );
                this.response.components[tsoIndex].underlyingRegulatedTariff =
                    regulatedTariffCopy.obj;
            } else if (
                this.response.components[tsoIndex].underlyingRegulatedTariff
                    .originalValue.value === null
            ) {
                this.response.components[
                    tsoIndex
                ].underlyingRegulatedTariff.originalValue.value = new Decimal(
                    0
                );
            }
        }
    }

    public prefillCapacityInformation(tsoIndex, contractIndex) {
        var contract = this.selectedUnderlyingDeals[tsoIndex];
        if (this.tsoConfig[tsoIndex].balancingGroupDesired) {
            this.response.components[tsoIndex].balancingGroup =
                this.copyOrReset(
                    contract,
                    'balancingGroup' + contractIndex,
                    undefined
                ).obj;
        }
        if (this.tsoConfig[tsoIndex].portfolioCodeDesired) {
            this.response.components[tsoIndex].portfolioCode = this.copyOrReset(
                contract,
                'portfolioCode' + contractIndex,
                undefined
            ).obj;
        }
    }

    public copyOrReset(fromObj, from, resetValue) {
        let result = { obj: null, isReset: false };
        if (!!fromObj && !!fromObj[from]) {
            result.obj = angular.copy(fromObj[from]);
        } else {
            result.obj = resetValue;
            result.isReset = true;
        }
        return result;
    }

    /**
     * Submit a response to a proposal to buy or sell.
     *
     * @param form
     * @param {boolean} isValid true if Angular validation succeeded, false otherwise.
     * @returns {boolean} True if the a call to the service endpoint was sent, false otherwise.
     */
    public submitProposalResponse(form: ng.IFormController) {
        const inputValid = this.inputsValid();
        this.ui.isRegulatedTariffUpdateStateValid =
            this.isRegulatedTariffUpdateStateValid();
        if (
            !form.$valid ||
            !inputValid ||
            !this.ui.isRegulatedTariffUpdateStateValid
        ) {
            this.$window.scrollTo(0, 0);
            return false;
        }

        this.buildResponse();

        this.formSubmitting = true;
        this.TradeProposals.postRespond(
            this.response,
            (res) => {
                const responseResult = res;
                const appBaseUrl = config.appBaseUrl;
                this.proposalDetails.isRespondPost = true;
                this.proposalDetails.isRespondErrorPost = false;

                if (angular.equals(responseResult.proposalState, 'FULFILLED')) {
                    //if a finished trade is returned, it means that it got automatically accepted, in this case redirect to detail page
                    location.replace(
                        `${appBaseUrl}/reporting/secondary/trades/${responseResult.tradeId}`
                    );
                    location.reload();
                } else {
                    form.$setPristine();
                    this.$route.updateParams({
                        action: this.createProposalService.DETAIL_PAGE_ACTION
                            .CREATE_RESPONSE,
                        tradeId: res.tradeId //tradeId isn't needed, but update of page will only happen, if route changes
                    });
                }
            },
            (res) => {
                this.$window.scrollTo(0, 0);
                this.proposalDetails.isRespondPost = false;
                this.proposalDetails.isRespondErrorPost = true;
                this.proposalDetails.errorMessage =
                    res && res.data && res.data.message
                        ? res.data.message
                        : 'An unknown error occured!';
            }
        ).$promise.finally(() => {
            this.formSubmitting = false;
        });
        return true;
    }

    public isRegulatedTariffUpdateStateValid() {
        return (
            this.isTransferOfUse ||
            ((!this.tsoConfig[0] ||
                !this.tsoConfig[0].updateRegulatedTariffAllowed ||
                !this.ui.renderUpdateButton0 ||
                this.ui.isTariffUpdated0 ||
                this.proposalDetails.ui.updateRegulatedTariffFailed0) &&
                (!this.proposal.networkPoint.bundled ||
                    !this.tsoConfig[1] ||
                    !this.tsoConfig[1].updateRegulatedTariffAllowed ||
                    !this.ui.renderUpdateButton1 ||
                    this.ui.isTariffUpdated1 ||
                    this.proposalDetails.ui.updateRegulatedTariffFailed1))
        );
    }

    public buildResponse() {
        this.response.id = this.proposalId;
        this.response.price.originalUnit = this.proposal.price.originalUnit;

        if (this.proposal.tradingProcedure === 'CFO') {
            this.response.expirationDate =
                typeof this.expirationDate === 'string'
                    ? this.expirationDate
                    : this.expirationDate.toISOString();
        }

        this.response.runtime = {
            start: this.runtimeObj.start,
            end: this.runtimeObj.end
        };

        this.prepareComponent(0);
        if (this.proposal.networkPoint.bundled) {
            this.prepareComponent(1);
        }
    }

    public prepareComponent(index) {
        const component = this.response.components[index];

        if (this['regulatedTariff' + index]) {
            delete component.underlyingRegulatedTariff;
        }

        if (!this.isManualCapacitySource(index)) {
            if (!!this.selectedUnderlyingDeals[index]) {
                component.underlyingDealId.identifier =
                    this.selectedUnderlyingDeals[index].dealId;
            }
        }

        if (!!this.response['balancingGroup' + index]) {
            component.balancingGroup = this.response['balancingGroup' + index];
        }
        if (!!this.response['portfolioCode' + index]) {
            component.portfolioCode = this.response['portfolioCode' + index];
        }

        if (!this.isSellIntention) {
            if (!angular.isUndefined(component.underlyingRegulatedTariff)) {
                if (
                    component.underlyingRegulatedTariff.originalValue.value ===
                    null
                ) {
                    delete component.underlyingRegulatedTariff;
                }
            }

            if (component.underlyingSurcharge.originalValue.value === null) {
                delete component.underlyingSurcharge;
            }
        }
    }

    /**
     * Bound to the "Update" button updates the regulated capacity tariff.
     */
    public updateUnderlyingTariff(index) {
        this.ui['isTariffUpdating' + index] = true;

        this.TradeProposals.updateUnderlyingTariff(
            {
                underlyingDeal: this.selectedUnderlyingDeals[index],
                direction: index === 0 ? 'EXIT' : 'ENTRY',
                combineUnbundled: !!this.response.combineUnbundledDeals,
                // When the combineUnbundledDeals is undefined the toggle will be false
                bundled: this.proposal.networkPoint.bundled
            },
            (res) => {
                this.response.components[index].underlyingRegulatedTariff = res;
                this.ui['isTariffUpdated' + index] = true;
                this.proposalDetails.uiErrorTariffNotUpdated = false;
            },
            () => {
                this.proposalDetails.ui['updateRegulatedTariffFailed' + index] =
                    true;
            }
        ).$promise.finally(() => {
            this.ui['isTariffUpdating' + index] = false;
            this.ui.isRegulatedTariffUpdateStateValid =
                this.isRegulatedTariffUpdateStateValid();
        });
    }

    /**
     * Check if the user wants manual import for the response details.
     *
     * @returns {boolean} True, if a manual input was chosen as the capacity source, false otherwise.
     */
    public isManualCapacitySource(index) {
        if (this.response.components[index].underlyingDealId) {
            return (
                this.response.components[index].underlyingDealId.source ===
                this.UNDERLYING_DEAL_SOURCE.MANUAL_INPUT
            );
        }
        return false;
    }

    public isTsoConfigRegulatedTariffReadonly(index) {
        var checkConfig = this.config['tso' + index + 'Config'];

        if (
            checkConfig.prefillUnderlyingPrice == 'SUPPORTED' &&
            checkConfig.captureUnderlyingPriceManually == 'NOT_SUPPORTED' &&
            checkConfig.checkboxRegulatedTariff == 'NOT_SUPPORTED'
        ) {
            return true;
        }

        if (
            checkConfig.prefillUnderlyingPrice == 'NOT_SUPPORTED' &&
            checkConfig.captureUnderlyingPriceManually == 'NOT_SUPPORTED' &&
            checkConfig.checkboxRegulatedTariff == 'SUPPORTED'
        ) {
            return true;
        }

        return (
            checkConfig.prefillUnderlyingPrice == 'SUPPORTED' &&
            checkConfig.captureUnderlyingPriceManually == 'NOT_SUPPORTED' &&
            checkConfig.checkboxRegulatedTariff == 'SUPPORTED'
        );
    }

    public checkRenderUpdateButton(index) {
        var capacitySource =
            this.response.components[index].underlyingDealId.source;
        if (
            this.tsoConfig[index].updateRegulatedTariffAllowed &&
            capacitySource !== undefined
        ) {
            this.ui['renderUpdateButton' + index] =
                capacitySource ===
                    this.createProposalService.CAPACITY_SOURCE
                        .PRIMARY_DEAL_ID ||
                capacitySource ===
                    this.createProposalService.CAPACITY_SOURCE
                        .SECONDARY_TRADE_ID;
        }
    }

    public fetchConfig() {
        return this.TradeProposals.tsoConfig(
            {
                networkPointId: this.proposal.networkPoint.id
            },
            (res) => {
                this.config = res;
            }
        ).$promise;
    }

    public fetchChannelConfig() {
        return this.Shipper.channelConfig({}, (res) => {
            this.channelConfig = res;
        }).$promise;
    }

    public fetchInfoLink() {
        return this.Shipper.remitInfoLink({}, (res) => {
            this.remitInfoLink = res.remitLink;
        });
    }

    public fetchBalancingCodes(
        runtime,
        networkPoint,
        capacityCategory0,
        capacityCategory1
    ) {
        const param = {
            runtimeStart: runtime.start,
            runtimeEnd: runtime.end,
            networkPointId: networkPoint.id,
            capacityCategory0: capacityCategory0,
            capacityCategory1: capacityCategory1
        };

        return this.TradeProposals.balancingCodes(param, (res) => {
            this.balancingCodeInfo = res;
        }).$promise;
    }

    public mapConfigAndOptions() {
        this.tsoConfig = [this.buildTsoConfig(0)];
        this.options = [this.buildOptions(0)];
        if (this.proposal.networkPoint.bundled) {
            this.tsoConfig[1] = this.buildTsoConfig(1);
            this.options[1] = this.buildOptions(1);
        }
    }

    public buildTsoConfig(index) {
        const tsoConfig = this.config['tso' + index + 'Config'];
        return this.createProposalService.buildTsoConfig(
            index,
            tsoConfig,
            this.balancingCodeInfo
        );
    }

    public buildOptions(index) {
        const tsoConfig = this.config['tso' + index + 'Config'];
        return this.createProposalService.buildOptions(
            index,
            tsoConfig,
            this.balancingCodeInfo,
            this.proposal.networkPoint
        );
    }

    public inputsValid() {
        var validRuntime = this.isRuntimeValid();
        var validPriceInformation = this.isPriceInformationValid();
        var validDeadline = this.isDeadlineValid();
        var isMinimumTradeRuntimeValid = this.validateMinimumTradeRuntime();
        return (
            validRuntime &&
            validPriceInformation &&
            validDeadline &&
            !this.minimumLotRuntimeViolation &&
            isMinimumTradeRuntimeValid
        );
    }

    // most likely the same as createProposalStageThree#isDateInputCombinationValid
    public isRuntimeValid() {
        const now = moment();
        const todayGasDayStart = this.utilService.getCurrentGasDayStart();
        const runtimeStart = moment(this.runtimeObj.start);
        const runtimeEnd = moment(this.runtimeObj.end);
        const proposalRuntimeStart = moment(this.proposal.runtime.start);
        const proposalRuntimeEnd = moment(this.proposal.runtime.end);

        this.ui.isRuntimeValid = runtimeStart.isValid() && runtimeEnd.isValid();
        if (!this.ui.isRuntimeValid) {
            return false;
        }

        this.ui.isRuntimeInProposal =
            runtimeStart.isSameOrAfter(proposalRuntimeStart, 'minute') &&
            runtimeStart.isBefore(proposalRuntimeEnd, 'minute') &&
            runtimeEnd.isSameOrBefore(proposalRuntimeEnd, 'minute') &&
            runtimeEnd.isAfter(proposalRuntimeStart, 'minute');

        // CR 309
        const isRuntimeStartInPast = runtimeStart.isBefore(now, 'minute');
        if (
            !angular.isUndefined(this.config) &&
            this.config.intraDayProposalAllowed
        ) {
            this.ui.isRuntimeStartGasDayOrAfter = runtimeStart.isSameOrAfter(
                todayGasDayStart,
                'minute'
            );
        } else {
            this.ui.isRuntimeStartInPast = isRuntimeStartInPast;
        }

        return (
            this.ui.isRuntimeValid &&
            this.ui.isRuntimeInProposal &&
            this.ui.isRuntimeStartGasDayOrAfter &&
            !this.ui.isRuntimeStartInPast
        );
    }

    public getMinimumRuntime() {
        if (
            !!this.config.tso0Config &&
            !!this.config.tso1Config &&
            !!this.config.tso0Config.minimumTradeRuntime &&
            !!this.config.tso1Config.minimumTradeRuntime
        ) {
            return this.calculateMinimumRuntime(
                this.config.tso0Config.minimumTradeRuntime,
                this.config.tso1Config.minimumTradeRuntime
            );
        }
        return this.config.tso0Config.minimumTradeRuntime;
    }

    public calculateMinimumRuntime(minimumTradeRuntime0, minimumTradeRuntime1) {
        var periodType0AsNumber = this.convertPeriodTypeToNumber(
            minimumTradeRuntime0.type
        );
        var periodType1AsNumber = this.convertPeriodTypeToNumber(
            minimumTradeRuntime1.type
        );
        if (periodType0AsNumber === periodType1AsNumber) {
            return minimumTradeRuntime0.value < minimumTradeRuntime1.value
                ? minimumTradeRuntime1
                : minimumTradeRuntime0;
        } else {
            return periodType0AsNumber < periodType1AsNumber
                ? minimumTradeRuntime1
                : minimumTradeRuntime0;
        }
    }

    public convertPeriodTypeToNumber(periodType) {
        var result;
        switch (periodType) {
            case 'HOUR': {
                result = 1;
                break;
            }
            case 'DAY': {
                result = 2;
                break;
            }
            case 'MONTH': {
                result = 3;
                break;
            }
            default: {
                result = NaN;
            }
        }
        return result;
    }

    public validateMinimumTradeRuntime() {
        if (!this.runtimeObj) {
            return false;
        }

        const runtimeStart = moment(this.runtimeObj.start);
        const runtimeEnd = moment(this.runtimeObj.end);

        const errors0 = this.checkMinimumTradeRuntime(
            runtimeStart,
            runtimeEnd,
            this.config.tso0Config
        );
        let errors1 = [];

        if (!angular.isUndefined(this.config.tso1Config)) {
            errors1 = this.checkMinimumTradeRuntime(
                runtimeStart,
                runtimeEnd,
                this.config.tso1Config
            );
        }

        const errors = this.removeDoubles(errors0.concat(errors1));
        this.ui.isMinimumTradeRuntimeValid = errors.length === 0;
        this.ui.isMinimumTradeRuntimeValidityMessages = errors.join('<br/>');

        return this.ui.isMinimumTradeRuntimeValid;
    }

    public checkMinimumTradeRuntime(runtimeStart, runtimeEnd, tsoConfig) {
        const periodType = tsoConfig.minimumTradeRuntime.type;
        const minimum = tsoConfig.minimumTradeRuntime.value;

        const checkStartResult = this.checkRuntimeType(
            'start',
            runtimeStart,
            periodType
        );
        const checkEndResult = this.checkRuntimeType(
            'end',
            runtimeEnd,
            periodType
        );
        const checkIntervalSizeResult = this.checkIntervalSize(
            runtimeStart,
            runtimeEnd,
            periodType,
            minimum
        );

        let errors = [];
        if (checkStartResult !== '') {
            errors.push(checkStartResult);
        }
        if (checkEndResult !== '') {
            errors.push(checkEndResult);
        }
        if (checkIntervalSizeResult !== '') {
            errors.push(checkIntervalSizeResult);
        }
        return errors;
    }

    public checkRuntimeType(key, runtimePoint, periodType) {
        let result = '';
        switch (periodType) {
            case 'HOUR': {
                if (!this.isFullHour(runtimePoint)) {
                    result = 'The runtime ' + key + ' is not a full hour';
                }
                break;
            }
            case 'DAY': {
                if (!this.isGasDay(runtimePoint)) {
                    result = 'The runtime ' + key + ' is not a gas day';
                }
                break;
            }
            case 'MONTH': {
                if (!this.isGasMonth(runtimePoint)) {
                    result = 'The runtime ' + key + ' is not a gas month';
                }
                break;
            }
            default: {
                result = 'The configured period type is invalid: ' + periodType;
            }
        }
        return result;
    }

    public checkIntervalSize(runtimeStart, runtimeEnd, periodType, minimum) {
        var result = '';
        switch (periodType) {
            case 'HOUR': {
                if (this.durationInHours(runtimeStart, runtimeEnd) < minimum) {
                    result =
                        'The proposal runtime is smaller than the minimum trade runtime: ' +
                        minimum +
                        ' hour(s)';
                }
                break;
            }
            case 'DAY': {
                if (this.durationInDays(runtimeStart, runtimeEnd) < minimum) {
                    result =
                        'The proposal runtime is smaller than the minimum trade runtime: ' +
                        minimum +
                        ' day(s)';
                }
                break;
            }
            case 'MONTH': {
                if (this.durationInMonths(runtimeStart, runtimeEnd) < minimum) {
                    result =
                        'The proposal runtime is smaller than the minimum trade runtime: ' +
                        minimum +
                        ' month(s)';
                }
                break;
            }
            default: {
                result = 'The configured period type is invalid: ' + periodType;
            }
        }
        return result;
    }

    public isFullHour(date) {
        var dateCet = moment(date);
        return (
            dateCet.minute() === 0 &&
            dateCet.second() === 0 &&
            dateCet.millisecond() === 0
        );
    }

    public isGasDay(date) {
        var dateCet = moment(date);
        return dateCet.hour() === 6 && this.isFullHour(dateCet);
    }

    public isGasMonth(date) {
        var dateCet = moment(date);
        return dateCet.date() === 1 && this.isGasDay(dateCet);
    }

    public durationInHours(start, end) {
        return end.diff(start, 'hours');
    }

    public durationInDays(start, end) {
        return end.diff(start, 'days');
    }

    public durationInMonths(start, end) {
        return end.diff(start, 'months');
    }

    public removeDoubles(array) {
        let result = [];
        angular.forEach(array, (value) => {
            if (result.indexOf(value) < 0) {
                result.push(value);
            }
        });
        return result;
    }

    public isPriceInformationValid() {
        // Proposal to sell: Response price >= proposal price
        // Proposal to buy:  Response price <= proposal price
        var result = true;

        if (
            !angular.isUndefined(this.response.price.originalValue) &&
            !angular.isUndefined(this.response.price.originalValue.value)
        ) {
            var responsePriceDecimal = new Decimal(
                this.response.price.originalValue.value
            );
            if (this.isSellIntention) {
                result = responsePriceDecimal.greaterThanOrEqualTo(
                    this.proposal.price.originalValue.value
                );
            } else {
                result = responsePriceDecimal.lessThanOrEqualTo(
                    this.proposal.price.originalValue.value
                );
            }
            this.evaluteError(
                result,
                this.form.price,
                this.isSellIntention ? 'sell' : 'buy'
            );
        }

        return result;
    }

    public isDeadlineValid() {
        if (this.proposal.tradingProcedure === 'CFO') {
            var deadLine = this.getDeadline();
            var proposalExpiration = moment(this.proposal.expirationDate);
            var now = moment();

            this.ui.isExpirationDateSet = !!deadLine;
            this.ui.isDeadlineAfterExpiration = this.ui.isExpirationDateSet
                ? deadLine.isAfter(proposalExpiration, 'minute')
                : false;
            this.ui.isDeadlineCurrentOrPast = this.ui.isExpirationDateSet
                ? deadLine.isSameOrBefore(now, 'minute')
                : false;
        }

        return (
            this.ui.isExpirationDateSet &&
            !this.ui.isDeadlineAfterExpiration &&
            !this.ui.isDeadlineCurrentOrPast
        );
    }

    public evaluteError(isValid, formElement, msg) {
        return this.createProposalService.evaluteError(
            isValid,
            formElement,
            msg
        );
    }

    public getDeadline() {
        if (
            angular.isUndefined(this.expirationDate) ||
            this.expirationDate === null ||
            angular.equals({}, this.expirationDate)
        ) {
            return undefined;
        } else {
            return moment(this.expirationDate);
        }
    }

    public showFurtherInputs() {
        var show = true;

        if (!this.isSellIntention) {
            if (this.response.combineUnbundledDeals) {
                show =
                    this.isManualCapacitySource(0) ||
                    this.isManualCapacitySource(1) ||
                    !!this.selectedUnderlyingDeals[0] ||
                    !!this.selectedUnderlyingDeals[1];
            } else {
                show =
                    this.isManualCapacitySource(0) ||
                    !!this.selectedUnderlyingDeals[0];
            }
        }

        return show;
    }

    public areTwoDealIdsRequiredForBundledContract() {
        return (
            this.proposal.networkPoint.bundled &&
            !this.response.combineUnbundledDeals &&
            this.isManualCapacitySource('0') &&
            this.tsoConfig[0].underlyingDealDesired &&
            this.tsoConfig[1].underlyingDealDesired
        );
    }
}

angular.module('app').controller('ProposalResponseCtrl', ProposalResponseCtrl);
